// DeskBriefs — what stands before export and what came of a generate, on
// the Studio's pages (pen "Studio – the desk"; a planned page shows only its
// title and Planned: it is filled from a source, never from a free brief,
// review of 29 Oct):
//  - BeforeExportRows: what stands before export, as rows in the Studio's
//    Needs you tab, with "Generate it" while documents are planned;
//  - NoteLine: what came of a generate, in place (deskFacts.DeskNote);
//  - WritingMiddle: an open document being written (the B2 skeleton).
// Pure: facts and notes are projected in; each change goes out as an intent.

export { BeforeExportRows, beforeExportItems, NoteLine, WritingMiddle };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { ExportReadiness } from "../../app/ports/brief.js";
import { Icon } from "../Icon/Icon.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import {
    type DeskNote,
    type ReadFailure,
    type ReadinessItem,
    readinessItems,
} from "./deskFacts.js";

// Asked, or reading the sources: Generate waits.
const busy = (note: DeskNote | undefined): boolean =>
    note?.type === "starting" || note?.type === "reading";

// Nothing was written; each reason names the way forward (the material
// side view's words, StatusLine.tsx).
const ReadFailedText = ({
    reason,
}: {
    readonly reason: ReadFailure;
}): string => {
    const { t } = useLingui();
    switch (reason) {
        case "sources_unreadable":
            return t`Nothing written: the sources have no readable text yet. Wait until they are read in, or add a source with text, then generate again.`;
        case "no_claims_found":
            return t`Nothing written: no facts could be read from the sources. Add a source that covers this material, then generate again.`;
        case "reading_failed":
        case "unknown":
            return t`Nothing written: reading the sources failed. Generate again to retry.`;
        default:
            return exhaustSwitchCase(reason);
    }
};

const NoteLine = ({ note }: { readonly note: DeskNote }): ReactNode => {
    const { t } = useLingui();
    const text = ((): ReactNode => {
        switch (note.type) {
            case "starting":
                return t`Starting …`;
            case "reading":
                return t`Reading the sources …`;
            case "read_failed":
                return <ReadFailedText reason={note.reason} />;
            case "no_sources":
                return t`Add a source to this module first`;
            case "already_writing":
                return t`This is being written already`;
            case "brief_locked":
                return t`Generated already: the brief stays as it is for now`;
            case "failed":
                return note.text;
            default:
                return exhaustSwitchCase(note);
        }
    })();
    return (
        <p className="bilda-desk__note" data-note={note.type} role="status">
            <Icon
                name={
                    note.type === "starting" || note.type === "reading"
                        ? "sparkle"
                        : "info"
                }
                size="sm"
            />
            {text}
        </p>
    );
};

const ItemText = ({ item }: { readonly item: ReadinessItem }): string => {
    const { t } = useLingui();
    switch (item.type) {
        case "planned":
            return t`${plural(item.count, {
                one: "# document planned, not written",
                other: "# documents planned, not written",
            })}`;
        case "empty":
            return t`${plural(item.count, {
                one: "# document empty",
                other: "# documents empty",
            })}`;
        case "removed":
            return t`${plural(item.count, {
                one: "# document removed elsewhere",
                other: "# documents removed elsewhere",
            })}`;
        case "moved": {
            const { target } = item;
            return t`${plural(item.count, {
                one: `# part changes place in ${target}`,
                other: `# parts change place in ${target}`,
            })}`;
        }
        case "unavailable": {
            const { target } = item;
            return t`${target} cannot take it`;
        }
        case "loss": {
            const { target } = item;
            switch (item.loss) {
                case "lost_interactivity":
                    return t`interactive parts become text in ${target}`;
                case "lost_objective_alignment":
                    return t`goals are left out in ${target}`;
                case "lost_grading_logic":
                    return t`grading is left out in ${target}`;
                case "lost_styling":
                    return t`styling is simplified in ${target}`;
                case "lost_audio_metadata":
                    return t`audio details are left out in ${target}`;
                case "other":
                    return t`some things change in ${target}`;
                default:
                    return exhaustSwitchCase(item.loss);
            }
        }
        default:
            return exhaustSwitchCase(item);
    }
};

// What stands before export that the Needs you tab's Fill rows do not say
// already (they name planned, empty and removed parts): what the target
// format leaves out or moves, a target that cannot take it, and the planned
// documents "Generate it" writes. Each case explicit; nothing else shows.
const beforeExportItems = (
    readiness: ExportReadiness,
): readonly ReadinessItem[] =>
    readinessItems(readiness).filter((item) => {
        switch (item.type) {
            case "loss":
            case "moved":
            case "unavailable":
            case "planned":
                return true;
            case "empty":
            case "removed":
                return false;
            default:
                return exhaustSwitchCase(item);
        }
    });

// The Needs you tab's "Before export" rows (they stood as a banner at a
// container page's foot until the owner's review, 29 Oct): one row per
// item. The planned one fills from the project's sources ("Generate it");
// with no source yet it offers Add a source instead (nothing is made
// without one; ui-brief, 29 Oct).
const BeforeExportRows = ({
    items,
    where,
    planned,
    note,
    online,
    sourced,
    onGenerateAll,
    onAddSource,
}: {
    readonly items: readonly ReadinessItem[];
    // The course or module the check is for.
    readonly where: string;
    // What "Generate it" writes (deskFacts.toGenerate).
    readonly planned: readonly string[];
    readonly note: DeskNote | undefined;
    readonly online: boolean;
    // The project has a source to fill from.
    readonly sourced: boolean;
    readonly onGenerateAll: (ids: readonly string[]) => void;
    readonly onAddSource: () => void;
}): ReactNode => {
    const { t } = useLingui();
    return items.map((item) => (
        <li key={JSON.stringify(item)}>
            <div className="bilda-studio-export__row">
                <span
                    className="bilda-studio-export__icon"
                    data-icon="info"
                    aria-hidden="true"
                >
                    <Icon name="info" />
                </span>
                <span className="bilda-studio-export__text">
                    <span className="bilda-studio-export__what">
                        <ItemText item={item} />
                    </span>
                    <span className="bilda-studio-export__where" title={where}>
                        {where}
                    </span>
                </span>
                {item.type !== "planned" || planned.length === 0 ? null : (
                    <button
                        type="button"
                        className="bilda-desk__generate-it"
                        disabled={!online || (sourced && busy(note))}
                        onClick={
                            sourced ? () => onGenerateAll(planned) : onAddSource
                        }
                    >
                        {sourced ? t`Generate it` : t`Add a source`}
                    </button>
                )}
            </div>
        </li>
    ));
};

// Being written (or reading the sources first): the shape of a page,
// filling in, under its step line.
const WritingMiddle = ({
    reading,
}: {
    readonly reading: boolean;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div className="bilda-desk__writing" aria-busy="true">
            <p className="bilda-desk__state" data-state="writing" role="status">
                <Icon name="sparkle" size="xs" />
                {reading ? t`Reading the sources …` : t`Writing …`}
            </p>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="block" height="var(--space-16)" />
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="70%" />
        </div>
    );
};
